import React, { useState } from 'react';
import Modal from '../common/Modal';
import Input from '../common/Input';
import Button from '../common/Button';
import { transactionService } from '../../services/transactionService';
import { useNotification } from '../../hooks/useNotification';

const RecordPaymentModal = ({ isOpen, onClose, debtor, onSuccess }) => {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    paymentMethod: 'cash',
    notes: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const paymentAmount = parseFloat(formData.amount);

    // Validate payment doesn't exceed outstanding
    if (paymentAmount > debtor.totalOutstanding) {
      showError(`Payment amount cannot exceed outstanding balance (${debtor.totalOutstanding.toFixed(3)} BHD)`);
      return;
    }

    setLoading(true);

    try {
      await transactionService.create(debtor._id, {
        type: 'payment',
        amount: paymentAmount,
        paymentMethod: formData.paymentMethod,
        notes: formData.notes
      });

      showSuccess('Payment recorded successfully!');
      setFormData({ amount: '', paymentMethod: 'cash', notes: '' });
      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error recording payment:', error);
      showError(error.message || 'Failed to record payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ amount: '', paymentMethod: 'cash', notes: '' });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Record Payment">
      <div className="mb-4">
        <p className="text-sm text-gray-600">
          Recording payment from: <strong>{debtor?.name}</strong>
        </p>
        <p className="text-sm text-gray-600">
          Outstanding balance: <strong className="text-red-600">{debtor?.totalOutstanding.toFixed(3)} BHD</strong>
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <Input
          label="Payment Amount (BHD)"
          type="number"
          step="0.001"
          name="amount"
          value={formData.amount}
          onChange={handleChange}
          placeholder="0.000"
          required
        />

        <div className="mb-4">
          <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700 mb-1">
            Payment Method <span className="text-red-500">*</span>
          </label>
          <select
            id="paymentMethod"
            name="paymentMethod"
            value={formData.paymentMethod}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="cash">Cash</option>
            <option value="benefit_card">Benefit/Card</option>
          </select>
        </div>

        <div className="mb-4">
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
            Notes (Optional)
          </label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            placeholder="Partial payment, full settlement, etc..."
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="flex gap-3 justify-end">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="success" loading={loading}>
            Record Payment
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default RecordPaymentModal;
